import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';

const inputClass = 'h-11 bg-surface border-contrast/[0.12] shadow-sm focus-visible:ring-2 focus-visible:ring-brand/20 focus-visible:border-brand';
const buttonClass = 'w-full h-11 bg-brand hover:bg-brand-soft text-white font-medium rounded-lg transition-all shadow-[0_8px_20px_-8px_rgba(37,99,235,0.55)] hover:shadow-[0_10px_24px_-8px_rgba(37,99,235,0.65)]';

export default function RegisterPage() {
  const { register, formatApiErrorDetail } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', organization: '', phone: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const result = await register(form);
      if (result.pending_approval) {
        setPendingApproval(true);
        return;
      }
      navigate('/partner');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (pendingApproval) {
    return (
      <AuthLayout title="Registration received" subtitle="Your account is pending admin approval" testId="register-page">
        <div className="text-center" data-testid="register-success">
          <div className="w-14 h-14 bg-success/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-success" />
          </div>
          <p className="text-ink-2 mb-6">
            We've emailed you a confirmation. You'll be able to sign in as soon as the XpertAdvisor team activates your account.
          </p>
          <Link to="/login" className={`${buttonClass} inline-flex items-center justify-center`}>Go to Sign In</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Become a partner" subtitle="Register your organization on the Partners Portal" testId="register-page" logoClass="h-36">
      {error && (
        <div role="alert" className="mb-4 p-3 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm" data-testid="register-error">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-ink font-medium">Full name</Label>
            <Input id="name" name="name" value={form.name} onChange={handleChange} placeholder="John Doe" required autoComplete="name" data-testid="register-name-input" className={inputClass} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-ink font-medium">Phone</Label>
            <Input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+91 98480 12345" autoComplete="tel" data-testid="register-phone-input" className={inputClass} />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="organization" className="text-ink font-medium">Organization</Label>
          <Input id="organization" name="organization" value={form.organization} onChange={handleChange} placeholder="Your college / agency" required autoComplete="organization" data-testid="register-org-input" className={inputClass} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="reg-email" className="text-ink font-medium">Email</Label>
          <Input id="reg-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@company.com" required autoComplete="email" data-testid="register-email-input" className={inputClass} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="reg-password" className="text-ink font-medium">Password</Label>
          <div className="relative">
            <Input id="reg-password" name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange} placeholder="At least 6 characters" required autoComplete="new-password" data-testid="register-password-input" className={`${inputClass} pr-10`} />
            <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-2">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Button type="submit" disabled={loading} data-testid="register-submit-button" className={buttonClass}>
          {loading ? 'Creating account...' : 'Create partner account'}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-ink-2">
        Already have an account?{' '}
        <Link to="/login" className="text-brand hover:text-brand-soft font-medium underline-offset-4 hover:underline" data-testid="login-link">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
